import { DndContext } from "@dnd-kit/core";
import { FileDownload } from "@mui/icons-material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { Box, CircularProgress, IconButton, Tooltip } from "@mui/material";
import { DateTime } from "luxon";
import { type ReactNode, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { InHouseTooltip } from "../../../components/InHouseTooltip/InHouseTooltip";
import { initialRecurringShiftDataStructure, RecurringShiftComponentSource } from "../../../components/RecurringShift/RecurringShift";
import { RecurringShiftPanelWrapper } from "../../../components/RecurringShift/RecurringShiftPanelWrapper";
import { DaysInWeek } from "../../../components/RecurringShift/util/DaysInWeekSelector";
import { GlobalLocationOrder } from "../../../components/staffing/Header/GlobalLocationOrder";
import { FilterActiveDot } from "../../../components/UIToolkit/Badges/FilterActiveDot";
import { SystemDropdown } from "../../../components/UIToolkit/Inputs/SystemDropDown";
import { DepartmentTypeChips } from "../../../components/UIToolkit/molecules/DepartmentTypeChips";
import { RoleOrJobSelector } from "../../../components/UIToolkit/molecules/RoleOrJobSelector";
import { TimeFilterChips } from "../../../components/UIToolkit/molecules/TimeFilterChips";
import { SystemSortingButtonWithPopup } from "../../../components/UIToolkit/Popups/SystemSortingButtonWithPopup/SystemSortingButtonWithPopup";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { handleDownload } from "../../../downloads/utils";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";
import {
	backendService,
	type ScheduleTemplateExportPayload,
	type ScheduleTemplateNurseCell,
	type ScheduleTemplateSection,
	type ScheduleTemplateSlot,
	type WeekDayCell,
} from "../../../services/backend.service";
import { masterScheduleSortingKey } from "../../../services/LocalStorageService";
import { STATUS_BADGE, SURFACE } from "../../../theme/tokens";
import { LONG_PTO_BUTTON_BOTTOM_RESERVE_PX } from "../LongPTOButton/consts";
import { ScheduleTemplateHeader } from "../ScheduleTemplateHeader";
import { RecurringMasterScheduleCellMenu } from "./auxillary/RecurringMasterScheduleCellMenu";
import { MASTER_SCHEDULE_ROOT_ID } from "./consts";
import { MasterScheduleGrid } from "./Grid/MasterScheduleGrid";
import { useMasterScheduleV2State } from "./useMasterScheduleV2State";
import { contentCardStyle, masterScheduleDefaultSorting, masterScheduleSortingOptions, rootCardStyle } from "./utils";

const WEEK_START_DAY_OPTIONS = [
	{ value: 0, label: "Sun" },
	{ value: 1, label: "Mon" },
	{ value: 2, label: "Tue" },
	{ value: 3, label: "Wed" },
	{ value: 4, label: "Thu" },
	{ value: 5, label: "Fri" },
	{ value: 6, label: "Sat" },
];

export const MasterScheduleV2 = ({ trailing }: { trailing?: ReactNode }) => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { isAnyDemo, chosenDepartment: topLevelDepartment, getNurseFormattedName } = useHospitalData();
	const [isExportingTemplate, setIsExportingTemplate] = useState(false);

	const {
		weeksAndActiveState,
		firstDateOfCurrentWeek,
		selectedShiftFilter,
		handleShiftFilterChange,
		allShiftsTimes,
		offeredShiftWindows,
		filtersBoxRef,
		calculatedData,
		isCalculating,
		loadError,
		isStale,
		onShiftClick,
		onShiftKeyDown,
		currentlyClickedCell,
		editOrCreateRecurringShiftState,
		onShiftMenuAction,
		isDeleteLoading,
		handleOnAddClick,
		amountOfWeeks,
		chosenDepartment,
		handleRecurringShiftComponentOnClose,
		selectedRoleFilters,
		onSelectedRoleFiltersChanged,
		departmentSpecificRoles,
		selectedJobFilters,
		onSelectedJobFiltersChanged,
		isJobFilterActive,
		selectedDepartmentTypes,
		onSelectedDepartmentTypesChanged,
		recurringDataInWeeks,
		refetchRecurringShifts,
		getNurseById,
		handleNurseFilterChange,
		selectedNurseToFilterBy,
		masterScheduleSortState,
		setMasterScheduleSortState,
		handleSectionCollapseToggle,
		toggleCollapseAll,
		expandedGroups,
		timezone,
		handleSortHeaderClicked,
		sortingMenuRef,
		uniqueSections,
		activeDragId,
		activeDragData,
		handleDragStart,
		handleDragEnd,
		sensors,
		onCtrlPressedChange,
		recurringDataInWeeksWithoutIntervalLimit,
		isSynergyMS,
		sectionOrder,
		isDraggingSection,
		allSectionsCollapsed,
		showAllLocationsSections,
		toggleShowAllLocations,
		availableLocations,
		selectedLocationFilters,
		onSelectedLocationFiltersChanged,
		weekStartDay,
		setWeekStartDay,
	} = useMasterScheduleV2State();
	const isRuntimeWeekStartEnabled = useFeatureFlag(FEATURE_FLAGS.ScheduleTemplateRuntimeWeekStart);
	const isHiringModuleEnabled = useFeatureFlag(FEATURE_FLAGS.HiringModule);

	const locationDropdownItems = availableLocations.map((loc) => ({
		value: loc,
		label: loc || t("schedule.template.no_location"),
	}));

	const handleExportTemplate = useCallback(async () => {
		const deptId = topLevelDepartment?.department_id;
		if (!deptId || !calculatedData) return;
		track("schedule_template_export_start", { department_id: deptId });
		setIsExportingTemplate(true);
		try {
			const dayAbbrevToIndex: Record<string, number> = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };

			// Sort sections the same way the grid does (sectionOrder first, then alphabetical)
			// Object.keys() puts numeric keys like "2" first, so we must re-sort here.
			const orderedSections = [...Object.keys(calculatedData)].sort((a, b) => {
				const aIndex = sectionOrder.indexOf(a);
				const bIndex = sectionOrder.indexOf(b);
				if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
				if (aIndex !== -1) return -1;
				if (bIndex !== -1) return 1;
				return a.localeCompare(b);
			});

			const sections: ScheduleTemplateSection[] = orderedSections.map((sectionName) => {
				const scheduleDataList = calculatedData[sectionName] ?? [];
				const slots: ScheduleTemplateSlot[] = scheduleDataList.map((sd) => {
					const loc = sd.location;
					const configuredDays = new Set(loc.days.map((d) => dayAbbrevToIndex[d.toLowerCase().substring(0, 3)] ?? -1).filter((i) => i >= 0));
					const configuredWeeks = loc.week_numbers?.length
						? new Set(loc.week_numbers.filter((w) => w >= 1 && w <= amountOfWeeks).map((w) => w - 1))
						: new Set(Array.from({ length: amountOfWeeks }, (_, i) => i));
					const configuredCells: WeekDayCell[] = [];
					for (const w of configuredWeeks) {
						for (const d of configuredDays) {
							configuredCells.push({ week_idx: w, day_idx: d });
						}
					}

					// Collect nurses and their cell positions from weeklyShifts
					const nurseCellsMap = new Map<number, { name: string; cells: Set<string> }>();
					for (let weekIdx = 0; weekIdx < sd.weeklyShifts.length; weekIdx++) {
						const week = sd.weeklyShifts[weekIdx];
						for (let dayIdx = 0; dayIdx < week.days.length; dayIdx++) {
							const day = week.days[dayIdx];
							for (const shiftInCell of day.recurringShiftsInCell) {
								if (shiftInCell.isFutureShift) continue;
								const nurseId = shiftInCell.recurringShift.nurseId;
								const existing = nurseCellsMap.get(nurseId);
								if (existing) {
									existing.cells.add(`${weekIdx},${dayIdx}`);
								} else {
									nurseCellsMap.set(nurseId, { name: getNurseFormattedName(nurseId), cells: new Set([`${weekIdx},${dayIdx}`]) });
								}
							}
						}
					}

					const nurseCells: ScheduleTemplateNurseCell[] = Array.from(nurseCellsMap.entries()).map(([, { name, cells }]) => ({
						nurse_name: name,
						cells: Array.from(cells).map((key) => {
							const [weekIdx, dayIdx] = key.split(",").map(Number);
							return { week_idx: weekIdx, day_idx: dayIdx };
						}),
					}));

					return {
						location_display: loc.location || "No Location",
						shift_start: loc.shift_start.substring(0, 5),
						shift_end: loc.shift_end.substring(0, 5),
						amount: loc.amount ?? 0,
						is_outside_config: sd.isLocationOutsideOfConfig ?? false,
						configured_cells: configuredCells,
						nurse_cells: nurseCells,
					};
				});
				return { section_name: sectionName, slots };
			});

			const payload: ScheduleTemplateExportPayload = {
				department_id: deptId,
				master_schedule_weeks: amountOfWeeks,
				first_sunday: chosenDepartment?.first_sunday?.toFormat("yyyy-MM-dd") ?? "",
				sections,
			};

			const response = await backendService.postScheduleTemplateExport(payload);
			const deptName = (topLevelDepartment.display_name || topLevelDepartment.name || "template").replace(/\s+/g, "_").toLowerCase();
			handleDownload(response.data, `${deptName}_schedule_template.xlsx`);
			track("schedule_template_export_success", { department_id: deptId });
		} catch (error) {
			trackError("schedule_template_export_error", { department_id: deptId, error_message: (error as Error)?.message });
		} finally {
			setIsExportingTemplate(false);
		}
	}, [topLevelDepartment, calculatedData, sectionOrder, amountOfWeeks, chosenDepartment, getNurseFormattedName, track, trackError]);

	return (
		<DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd} sensors={sensors}>
			<Box
				id={MASTER_SCHEDULE_ROOT_ID}
				data-testid="master-schedule-root"
				data-dragging-section={isDraggingSection}
				data-stale={isStale}
				sx={(theme) => ({
					...rootCardStyle,
					"& .ms2-highlight": {
						/* This styling is important and is used to highlight cells in ClickableShiftCell.tsx */
						backgroundColor: STATUS_BADGE.PEACH,
					},
					/* While a filter recompute is pending (deferred), fade the still-stale grid to signal it's updating. */
					"& #master-schedule-v2-grid": {
						transition: "opacity 200ms ease",
					},
					"&[data-stale='true'] #master-schedule-v2-grid": {
						opacity: 0.45,
						pointerEvents: "none",
					},
					"&:not([data-dragging-section='true']) .ms2-col-header-hovered": {
						backgroundColor: theme.palette.primary.light,
						color: SURFACE,
						fontWeight: 600,
						transition: "all 0ms cubic-bezier(0.4,0,0.2,1)",
						zIndex: 3,
					},
					/* Highlight week header when hovering over any cell in that week */
					[`&#${MASTER_SCHEDULE_ROOT_ID}:not([data-dragging-section='true']):has(#master-schedule-v2-grid .ms2-grid-cell[data-week-index='0']:hover) #master-schedule-v2-grid .ms2-week-header[data-week-index='0']`]:
						{
							backgroundColor: theme.palette.primary.light,
							color: SURFACE,
							fontWeight: 700,
							transition: "all 150ms",
							transform: "scale(1.02)",
							zIndex: 4,
						},
					[`&#${MASTER_SCHEDULE_ROOT_ID}:not([data-dragging-section='true']):has(#master-schedule-v2-grid .ms2-grid-cell[data-week-index='1']:hover) #master-schedule-v2-grid .ms2-week-header[data-week-index='1']`]:
						{
							backgroundColor: theme.palette.primary.light,
							color: SURFACE,
							fontWeight: 700,
							transition: "all 150ms",
							transform: "scale(1.02)",
							zIndex: 4,
						},
					[`&#${MASTER_SCHEDULE_ROOT_ID}:not([data-dragging-section='true']):has(#master-schedule-v2-grid .ms2-grid-cell[data-week-index='2']:hover) #master-schedule-v2-grid .ms2-week-header[data-week-index='2']`]:
						{
							backgroundColor: theme.palette.primary.light,
							color: SURFACE,
							fontWeight: 700,
							transition: "all 150ms",
							transform: "scale(1.02)",
							zIndex: 4,
						},
					[`&#${MASTER_SCHEDULE_ROOT_ID}:not([data-dragging-section='true']):has(#master-schedule-v2-grid .ms2-grid-cell[data-week-index='3']:hover) #master-schedule-v2-grid .ms2-week-header[data-week-index='3']`]:
						{
							backgroundColor: theme.palette.primary.light,
							color: SURFACE,
							fontWeight: 700,
							transition: "all 150ms",
							transform: "scale(1.02)",
							zIndex: 4,
						},
					/* Also highlight week header when hovering over column headers (day names) in that week */
					[`&#${MASTER_SCHEDULE_ROOT_ID}:not([data-dragging-section='true']):has(#master-schedule-v2-grid .ms2-col-header[data-week-index='0']:hover) #master-schedule-v2-grid .ms2-week-header[data-week-index='0']`]:
						{
							backgroundColor: theme.palette.primary.light,
							color: SURFACE,
							fontWeight: 700,
							transition: "all 150ms",
							transform: "scale(1.02)",
							zIndex: 4,
						},
					[`&#${MASTER_SCHEDULE_ROOT_ID}:not([data-dragging-section='true']):has(#master-schedule-v2-grid .ms2-col-header[data-week-index='1']:hover) #master-schedule-v2-grid .ms2-week-header[data-week-index='1']`]:
						{
							backgroundColor: theme.palette.primary.light,
							color: SURFACE,
							fontWeight: 700,
							transition: "all 150ms",
							transform: "scale(1.02)",
							zIndex: 4,
						},
					[`&#${MASTER_SCHEDULE_ROOT_ID}:not([data-dragging-section='true']):has(#master-schedule-v2-grid .ms2-col-header[data-week-index='2']:hover) #master-schedule-v2-grid .ms2-week-header[data-week-index='2']`]:
						{
							backgroundColor: theme.palette.primary.light,
							color: SURFACE,
							fontWeight: 700,
							transition: "all 150ms",
							transform: "scale(1.02)",
							zIndex: 4,
						},
					[`&#${MASTER_SCHEDULE_ROOT_ID}:not([data-dragging-section='true']):has(#master-schedule-v2-grid .ms2-col-header[data-week-index='3']:hover) #master-schedule-v2-grid .ms2-week-header[data-week-index='3']`]:
						{
							backgroundColor: theme.palette.primary.light,
							color: SURFACE,
							fontWeight: 700,
							transition: "all 150ms",
							transform: "scale(1.02)",
							zIndex: 4,
						},
				})}
			>
				{currentlyClickedCell && (
					<RecurringMasterScheduleCellMenu
						onSelectedAction={onShiftMenuAction}
						open={!!currentlyClickedCell}
						anchorEl={currentlyClickedCell.anchorEl}
						isDeleteLoading={isDeleteLoading}
					/>
				)}
				{editOrCreateRecurringShiftState && (
					<RecurringShiftPanelWrapper
						isSynergyMS={isSynergyMS}
						renderWithoutModal={false}
						clickedDate={editOrCreateRecurringShiftState.clickedDate}
						shouldAddNewShiftOnOpen={!editOrCreateRecurringShiftState.recurringShift?.nurseId}
						clickedNurseId={editOrCreateRecurringShiftState.recurringShift?.nurseId}
						recurringShiftsData={recurringDataInWeeksWithoutIntervalLimit ?? []}
						clickedLocation={editOrCreateRecurringShiftState.clickedLocation}
						onClose={handleRecurringShiftComponentOnClose}
						source={RecurringShiftComponentSource.MasterSchedule}
						recurringShiftDisplayMode={editOrCreateRecurringShiftState.mode}
						existingRecurringShiftId={editOrCreateRecurringShiftState.recurringShift?.id}
						existingShiftStartTime={
							editOrCreateRecurringShiftState.recurringShift
								? editOrCreateRecurringShiftState.clickedDate
										.set({ hour: +editOrCreateRecurringShiftState.recurringShift.shiftStart.substring(0, 2) })
										.set({ minute: +editOrCreateRecurringShiftState.recurringShift.shiftStart.substring(3, 5) })
								: undefined
						}
						initialRecurringShiftData={{
							...initialRecurringShiftDataStructure,
							recurrenceValue: 2,
							selectedLocation: editOrCreateRecurringShiftState.clickedLocation,
							...(!editOrCreateRecurringShiftState.recurringShift
								? {
										selectedDays: [
											DaysInWeek[editOrCreateRecurringShiftState.clickedDate.setLocale("en").toFormat("cccc").toLowerCase() as keyof typeof DaysInWeek],
										],
									}
								: {}),
							shiftStart: DateTime.fromFormat(editOrCreateRecurringShiftState.clickedLocation.shift_start, "h:mm").setZone(timezone),
							shiftEnd: DateTime.fromFormat(editOrCreateRecurringShiftState.clickedLocation.shift_end, "h:mm").setZone(timezone),
						}}
					/>
				)}
				<ScheduleTemplateHeader
					dataTestId="master-schedule-header"
					staffFilter={{ value: selectedNurseToFilterBy, onChange: handleNurseFilterChange }}
					trailing={trailing}
					actions={
						<InHouseTooltip title={t("schedule.template.export")}>
							<IconButton onClick={handleExportTemplate} disabled={isExportingTemplate} data-testid="master-schedule-export">
								{isExportingTemplate ? <CircularProgress size={20} /> : <FileDownload color="primary" />}
							</IconButton>
						</InHouseTooltip>
					}
				/>
				<Box
					sx={[
						{
							p: 2,
							// Bottom inset is deliberately larger than the other three sides: it reserves the footprint
							// of the `position: fixed` Long-PTO button (see LongPTOButton/consts.ts), which would
							// otherwise cover — and swallow clicks on — the grid's last row. Written out as
							// `paddingBottom` because the `pb` shorthand would multiply the px value by 8.
							paddingBottom: `${LONG_PTO_BUTTON_BOTTOM_RESERVE_PX}px`,
							// Let flex claim the leftover height rather than measuring the header. Subtracting only the
							// header ignored `rootCardStyle`'s 16px row gap, so this box ran 16px past the card and its
							// own bottom padding fell outside it — the grid sat flush against the window edge with the
							// last row jammed into it. `minHeight: 0` is what lets the grid inside scroll instead of
							// stretching this box back open.
							flex: 1,
							minHeight: 0,
						},
						...(Array.isArray(contentCardStyle) ? contentCardStyle : [contentCardStyle]),
					]}
				>
					<Box
						ref={filtersBoxRef}
						data-testid="master-schedule-filters"
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							width: "100%",
						}}
					>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 4,
							}}
						>
							{!isSynergyMS && (
								<TimeFilterChips
									allShiftsTimes={allShiftsTimes}
									exactTimes={offeredShiftWindows}
									selectedFilters={[selectedShiftFilter]}
									onSelectedFilterChange={handleShiftFilterChange}
								/>
							)}
							{isSynergyMS ? (
								// Keyed by department: the chips read their selection once at mount, and the page doesn't remount
								// on a department switch, so this is what lets each department's remembered filter show.
								<DepartmentTypeChips
									key={chosenDepartment.department_id}
									initiallySelected={selectedDepartmentTypes}
									onSelectedTypeFiltersChanged={onSelectedDepartmentTypesChanged}
								/>
							) : (
								<RoleOrJobSelector
									key={chosenDepartment.department_id}
									selectedRoles={selectedRoleFilters}
									onSelectedRolesChanged={onSelectedRoleFiltersChanged}
									availableRoles={departmentSpecificRoles}
									selectedJobIds={selectedJobFilters}
									onSelectedJobsChanged={onSelectedJobFiltersChanged}
									jobFilterActive={isJobFilterActive}
									label="Job"
									formControlSx={{ width: "200px" }}
								/>
							)}
							{availableLocations.length > 0 && (
								<FilterActiveDot active={selectedLocationFilters.length < availableLocations.length} dataTestId="master-schedule-location-filter-active-dot">
									<SystemDropdown
										multiple
										items={locationDropdownItems}
										selectedItems={selectedLocationFilters}
										onChangeMultiple={onSelectedLocationFiltersChanged}
										label="Location"
										showFilter
										formControlSx={{ width: "200px" }}
										data-testid="master-schedule-location-filter"
									/>
								</FilterActiveDot>
							)}
							{isRuntimeWeekStartEnabled && (
								<SystemDropdown
									items={WEEK_START_DAY_OPTIONS}
									selectedItem={weekStartDay}
									onChange={(v) => {
										if (v === undefined) return;
										setWeekStartDay(v);
										track("schedule_template_week_start_changed", { week_start_day: v });
									}}
									label={t("schedule.template.week_start")}
									formControlSx={{ minWidth: "140px" }}
									data-testid="master-schedule-week-start"
								/>
							)}
							<Box
								sx={{
									display: "flex",
									gap: 0.5,
									alignItems: "center",
								}}
							>
								<SystemSortingButtonWithPopup
									sortingOptions={isSynergyMS ? masterScheduleSortingOptions.filter((opt) => opt.label !== "Shift Times") : masterScheduleSortingOptions}
									initialState={masterScheduleSortState}
									setSortState={setMasterScheduleSortState}
									defaultSortState={isSynergyMS ? masterScheduleDefaultSorting.filter((opt) => opt.label !== "Shift Times") : masterScheduleDefaultSorting}
									localStorageTableKey={masterScheduleSortingKey}
									ref={sortingMenuRef}
								/>
								<Tooltip title={allSectionsCollapsed ? "Restore expanded sections" : "Collapse all sections"}>
									<IconButton onClick={toggleCollapseAll} size="small" sx={{ ml: 1 }} data-testid="master-schedule-collapse-all">
										{allSectionsCollapsed ? <UnfoldMoreIcon /> : <UnfoldLessIcon />}
									</IconButton>
								</Tooltip>
								<GlobalLocationOrder />
							</Box>
						</Box>
						<Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
							{(isAnyDemo || isHiringModuleEnabled) && (
								<SystemButton
									sx={{ p: 1, fontSize: "13px" }}
									onClick={() => navigate(`/${chosenDepartment?.department_id}/hiring`)}
									leadingIcon={<PersonAddIcon sx={{ fontSize: "18px" }} />}
									data-testid="master-schedule-hiring-btn"
								>
									{t("hiring.module.nav")}
								</SystemButton>
							)}
						</Box>
					</Box>

					{/*Grid Rendering*/}
					<MasterScheduleGrid
						amountOfWeeks={amountOfWeeks}
						filtersBoxElement={filtersBoxRef.current}
						weeksAndActiveState={weeksAndActiveState}
						handleSortHeaderClicked={handleSortHeaderClicked}
						masterScheduleSortState={masterScheduleSortState}
						departmentSpecificRoles={departmentSpecificRoles}
						refetchRecurringShifts={refetchRecurringShifts}
						uniqueSections={uniqueSections}
						firstDateOfCurrentWeek={firstDateOfCurrentWeek}
						calculatedData={calculatedData}
						isCalculating={isCalculating}
						loadError={loadError}
						chosenDepartment={chosenDepartment}
						expandedGroups={expandedGroups}
						handleSectionCollapseToggle={handleSectionCollapseToggle}
						recurringDataInWeeks={recurringDataInWeeks}
						getNurseById={getNurseById}
						activeDragId={activeDragId}
						activeDragData={activeDragData}
						onShiftKeyDown={onShiftKeyDown}
						handleOnAddClick={handleOnAddClick}
						onShiftClick={onShiftClick}
						onCtrlPressedChange={onCtrlPressedChange}
						isSynergyMS={isSynergyMS}
						sectionOrder={sectionOrder}
						isDraggingSection={isDraggingSection}
						showAllLocationsSections={showAllLocationsSections}
						toggleShowAllLocations={toggleShowAllLocations}
						weekStartDay={weekStartDay}
					/>
				</Box>
			</Box>
		</DndContext>
	);
};
